/** "What this filing states": the figures the report would put in front of the regulator, as a band of
 * tiles on the filing's own page (design review 2026-11-03, persona #3).
 *
 * A finance lead could not sign a filing from its page: the page carried a readiness percentage and a
 * to-do list and not one emissions figure; the stated total, the scopes and the energy figure were two
 * clicks away in Disclosures. This band is those lines, as the report states them.
 *
 * **It is the report's own lines, not a second list of what to show.** One tile per figure line the
 * regime requires (emissions, energy, intensity), in the regime's order and under the regime's own
 * label, so a regime loaded as data tomorrow gets its tiles with no edit here and nothing in this file
 * names a market. Nothing is summed: each figure is the server's string for that line. Four things it
 * keeps:
 *
 * - **A line EmitIQ cannot produce still gets a tile** ("Not assessed", never `0`), so an omission is
 *   not read as nothing being required. Scope 3 is the case.
 * - **A guidance-only line is marked and is not in the total** (B-19): the regulation names two
 *   activities and the guidance two scopes, and settling that by arithmetic is not this product's call.
 * - **Each figure opens its receipt.** One calculation opens that receipt; several open a short list of
 *   them, and a line that lists none (energy sums records, which have no calculation) opens the
 *   register for this company and period, where every figure is a row with its own receipt.
 * - **The Scope 2 basis is said once, on the band**, because a Scope 2 figure under one method is not
 *   the figure under the other.
 */

import { Link, useNavigate } from "@tanstack/react-router";
import { useState } from "react";

import type { Report, ReportLine } from "../../api/queries";
import { Drawer } from "../../components/ui/Drawer";
import { InfoPopover } from "../../components/ui/InfoPopover";
import { StatusChip } from "../../components/ui/StatusChip";
import { FiguresInRegister } from "../ReportsLines";
import { amount, factorLimitationsOf, receiptIdsOf } from "../reportsFormat";
import { keepSearch } from "../search";
import { FactorLimitation } from "./FactorLimitation";
import styles from "./FilingParts.module.css";
import { statedLines, statedValue } from "./stated";

function ReceiptsDrawer({
  line,
  report,
  obligationId,
  onClose,
}: {
  line: ReportLine;
  report: Report;
  obligationId: string;
  onClose: () => void;
}) {
  const { ids, truncated } = receiptIdsOf(line);
  return (
    <Drawer open onClose={onClose} title={line.label} width="sm" testId="stated-drawer">
      <div className={styles.drawerBody}>
        {ids.length > 0 ? (
          <ul className={styles.receiptList}>
            {ids.map((id, index) => (
              <li key={id}>
                <Link
                  to="/reports"
                  search={(prev: Record<string, unknown>) => ({
                    ...keepSearch(prev),
                    filing: obligationId,
                    receipt: id,
                  })}
                  onClick={onClose}
                  data-testid="stated-receipt-link"
                >
                  Receipt {index + 1}
                </Link>
              </li>
            ))}
          </ul>
        ) : null}
        {truncated ? (
          <p className={styles.quiet}>
            The first {ids.length} of {line.records} are listed.
          </p>
        ) : null}
        {factorLimitationsOf(line).map((item) => (
          <p key={item.kind} className={styles.quiet} data-testid="stated-factor-statement">
            {`${item.statement} It touches ${String(item.records)} ${item.records === 1 ? "figure" : "figures"} of this line, ${amount(item.tonnes, "tCO2e")} tCO₂e.`}
          </p>
        ))}
        <FiguresInRegister report={report} testId="stated-register">
          Every figure for {report.context.legal_entity_name} in this period
        </FiguresInRegister>
      </div>
    </Drawer>
  );
}

function StatedTile({ line, onOpen }: { line: ReportLine; onOpen: (line: ReportLine) => void }) {
  const shown = statedValue(line);
  const { ids } = receiptIdsOf(line);
  const guidance = line.basis === "guidance";
  const opens = shown.kind === "figure";
  return (
    <div className={`card ${styles.statedTile}`} data-tile={line.label} data-testid="stated-tile" data-key={line.key}>
      <span className={styles.statedLabel} title={line.label}>
        {line.label}
      </span>
      {opens ? (
        <button
          type="button"
          className={`${styles.statedFigure} num`}
          onClick={() => onOpen(line)}
          aria-label={`${line.label}: ${shown.text} ${shown.unit}. Show where this came from`}
          data-testid="stated-open"
          data-receipts={ids.length}
        >
          {shown.text}
          {shown.unit ? <span className={styles.statedUnit}>{shown.unit}</span> : null}
        </button>
      ) : (
        <span className={styles.statedWord} data-testid="stated-word">
          {shown.text}
        </span>
      )}
      {guidance ? (
        <StatusChip tone="neutral" size="sm">
          Guidance, not in the total
        </StatusChip>
      ) : null}
      <FactorLimitation line={line} />
    </div>
  );
}

export function StatedBand({ report, obligationId }: { report: Report; obligationId: string }) {
  const navigate = useNavigate();
  const [opened, setOpened] = useState<ReportLine | null>(null);
  const lines = statedLines(report.lines);
  if (lines.length === 0) return null;
  const method = report.scope2_method === "market" ? "market-based" : "location-based";

  const open = (line: ReportLine) => {
    const { ids } = receiptIdsOf(line);
    const only = ids.length === 1 ? ids[0] : undefined;
    if (only && !receiptIdsOf(line).truncated) {
      // One calculation is one receipt: the receipt drawer opens over the filing.
      void navigate({
        to: ".",
        search: (prev: Record<string, unknown>) => ({ ...prev, filing: obligationId, receipt: only }),
      });
      return;
    }
    setOpened(line);
  };

  return (
    <section className={styles.stated} aria-label="What this filing states" data-testid="stated-band">
      <div className={styles.statedHead}>
        <span className={styles.statedTitle} data-eyebrow>
          What this filing states
        </span>
        <StatusChip tone="info" size="sm" testId="stated-basis">
          Scope 2 {method}
        </StatusChip>
        <InfoPopover infoKey="filings.stated" />
      </div>
      <div className={styles.statedRow}>
        {lines.map((line) => (
          <StatedTile key={line.key} line={line} onOpen={open} />
        ))}
      </div>
      {opened ? (
        <ReceiptsDrawer line={opened} report={report} obligationId={obligationId} onClose={() => setOpened(null)} />
      ) : null}
    </section>
  );
}
